import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { ThemeContext } from '../../context/ThemeContext';
import { OmIcon, OmOverlayOpener, OmTable } from '@om/component-library-react';
import PaginationBlock from '../PaginationBlock';
import './FundDeclarationTable.scss';
import {
    getPaginationRanges,
    getPaginationTableSize,
    getIsApiFailure,
    getIsApiPending,
    getFundsFromSelection,
} from '../../../../redux/selectors/fundDeclarations/fundDeclarations';
import {
    setModalTypeValue,
    setPaginationSizeValue,
} from '@om/redux/actions/fundDeclarations/fundDeclarations';
import { hideChatBot } from '@om/redux/actions/publicWeb/chatBot';

const FundDeclarationTable = () => {
    const theme = React.useContext(ThemeContext);
    const dispatch = useDispatch();

    const {
        paginationRanges,
        paginationTableSize,
        fundData,
        isApiPending,
        isApiFailure,
    } = useSelector(
        createStructuredSelector({
            paginationRanges: getPaginationRanges,
            paginationTableSize: getPaginationTableSize,
            fundData: getFundsFromSelection,
            isApiPending: getIsApiPending,
            isApiFailure: getIsApiFailure,
        })
    );

    const paginatedFunds = (() => {
        return fundData.slice(0, paginationTableSize);
    })();

    const currentPaginatedValue = (() => {
        const maxNumberOfRecords = fundData.length;
        return paginationTableSize > maxNumberOfRecords
            ? maxNumberOfRecords
            : paginationTableSize;
    })();

    const paginationDropDownOptions = (() => {
        const ranges = paginationRanges
            .filter((value) => value < fundData.length)
            .map((value) => ({ label: value, value: value }));

        return [...ranges, { label: 'All', value: fundData.length }];
    })();

    const onPaginationSelect = (event) => {
        dispatch(setPaginationSizeValue(parseInt(event.detail.id)));
    };

    const overlayOpener = (modalType) => {
        dispatch(setModalTypeValue(modalType));
        dispatch(hideChatBot());
    };

    return (
        <div
            id="fund-declaration-table"
            className={`${theme} fund-declaration-table`}
        >
            <OmTable
                key={paginatedFunds.length}
                tableColumnsDesktop={4}
                tableHeaderRowsDesktop={1}
                tableBodyRowsDesktop={paginatedFunds.length}
                tablesMobile={paginatedFunds.length}
                tableHeaderRowsMobile={1}
                tableBodyColumnsMobile={2}
                tableBodyRowsMobile={3}
            >
                <h6 slot="desktop-table-header-row-1-cell-1">
                    <strong>Date</strong>
                </h6>
                <h6 className="small" slot="desktop-table-header-row-1-cell-2">
                    <div className="header-block">
                        <strong>Dividends cents per unit</strong>
                        <OmOverlayOpener
                            onOnOpen={() => overlayOpener('dividends')}
                            slot="more-info-button"
                            openingOverlay="fund-declaration-modal"
                        >
                            <OmIcon
                                iconName="help_outline"
                                width="20"
                                height="20"
                            />
                        </OmOverlayOpener>
                    </div>
                </h6>
                <h6 className="small" slot="desktop-table-header-row-1-cell-3">
                    <div className="header-block">
                        <strong>Interest cents per unit</strong>
                        <OmOverlayOpener
                            onOnOpen={() => overlayOpener('interest')}
                            slot="more-info-button"
                            openingOverlay="fund-declaration-modal"
                        >
                            <OmIcon
                                iconName="help_outline"
                                width="20"
                                height="20"
                            />
                        </OmOverlayOpener>
                    </div>
                </h6>
                <h6 className="small" slot="desktop-table-header-row-1-cell-4">
                    <div className="header-block">
                        <strong>Distribution cents per unit</strong>
                        <OmOverlayOpener
                            onOnOpen={() => overlayOpener('distribution')}
                            slot="more-info-button"
                            openingOverlay="fund-declaration-modal"
                        >
                            <OmIcon
                                iconName="help_outline"
                                width="20"
                                height="20"
                            />
                        </OmOverlayOpener>
                    </div>
                </h6>

                {paginatedFunds &&
                    paginatedFunds.map((data, index) => {
                        const dataPosition = index + 1;
                        return (
                            <React.Fragment>
                                <h6
                                    className="medium"
                                    slot={`desktop-table-body-row-${dataPosition}-cell-1`}
                                >
                                    <strong>{data.date}</strong>
                                </h6>
                                <h6
                                    className="medium small"
                                    slot={`desktop-table-body-row-${dataPosition}-cell-2`}
                                >
                                    {data.dividendsCentsPerUnit}
                                </h6>
                                <h6
                                    className="medium small"
                                    slot={`desktop-table-body-row-${dataPosition}-cell-3`}
                                >
                                    {data.interestCentsPerUnit}
                                </h6>
                                <h6
                                    className="medium small"
                                    slot={`desktop-table-body-row-${dataPosition}-cell-4`}
                                >
                                    {data.distributionCentsPerUnit}
                                </h6>

                                <h6
                                    slot={`mobile-table-${dataPosition}-header-row-1-cell-1`}
                                >
                                    <strong>{data.date}</strong>
                                </h6>

                                <h6
                                    slot={`mobile-table-${dataPosition}-body-row-1-cell-1`}
                                >
                                    <div className="header-block">
                                        <strong>Dividends c per unit</strong>
                                        <OmOverlayOpener
                                            onOnOpen={() =>
                                                overlayOpener('dividends')
                                            }
                                            slot="more-info-button"
                                            openingOverlay="fund-declaration-modal"
                                        >
                                            <OmIcon
                                                iconName="help_outline"
                                                width="20"
                                                height="20"
                                            />
                                        </OmOverlayOpener>
                                    </div>
                                </h6>
                                <h6
                                    slot={`mobile-table-${dataPosition}-body-row-2-cell-1`}
                                >
                                    <div className="header-block">
                                        <strong>Interest c per unit</strong>
                                        <OmOverlayOpener
                                            onOnOpen={() =>
                                                overlayOpener('interest')
                                            }
                                            slot="more-info-button"
                                            openingOverlay="fund-declaration-modal"
                                        >
                                            <OmIcon
                                                iconName="help_outline"
                                                width="20"
                                                height="20"
                                            />
                                        </OmOverlayOpener>
                                    </div>
                                </h6>
                                <h6
                                    slot={`mobile-table-${dataPosition}-body-row-3-cell-1`}
                                >
                                    <div className="header-block">
                                        <strong>Distribution c per unit</strong>
                                        <OmOverlayOpener
                                            onOnOpen={() =>
                                                overlayOpener('distribution')
                                            }
                                            slot="more-info-button"
                                            openingOverlay="fund-declaration-modal"
                                        >
                                            <OmIcon
                                                iconName="help_outline"
                                                width="20"
                                                height="20"
                                            />
                                        </OmOverlayOpener>
                                    </div>
                                </h6>

                                <h6
                                    slot={`mobile-table-${dataPosition}-body-row-1-cell-2`}
                                >
                                    {data.dividendsCentsPerUnit}
                                </h6>
                                <h6
                                    slot={`mobile-table-${dataPosition}-body-row-2-cell-2`}
                                >
                                    {data.interestCentsPerUnit}
                                </h6>
                                <h6
                                    slot={`mobile-table-${dataPosition}-body-row-3-cell-2`}
                                >
                                    {data.distributionCentsPerUnit}
                                </h6>
                            </React.Fragment>
                        );
                    })}
            </OmTable>
            {isApiFailure ? (
                <div className="no-funds-block">
                    <h5 className="no-bottom-margin no-top-margin">
                        There was a problem fetching fund data
                    </h5>
                    <h6 className="no-bottom-margin no-top-margin">
                        Please try again
                    </h6>
                </div>
            ) : isApiPending ? (
                <div className="no-funds-block">
                    <h5 className="no-bottom-margin no-top-margin">
                        Loading fund data
                    </h5>
                </div>
            ) : (
                <React.Fragment>
                    {fundData.length === 0 && (
                        <div className="no-funds-block">
                            <h5 className="no-bottom-margin no-top-margin">
                                No fund data to show
                            </h5>
                            <h6 className="no-bottom-margin no-top-margin">
                                Try another fund
                            </h6>
                        </div>
                    )}
                    {fundData.length > 0 && (
                        <PaginationBlock
                            slot="pagination-block"
                            theme={theme}
                            options={paginationDropDownOptions}
                            currentPaginatedValue={currentPaginatedValue}
                            numberOfRecords={fundData.length}
                            onSelectEventFunction={onPaginationSelect}
                            showPrintButton={false}
                        />
                    )}
                </React.Fragment>
            )}
        </div>
    );
};
export default FundDeclarationTable;
